/**
 * Proof Library Page
 * 
 * Epic 8: Manage portfolio items for proof-driven proposals.
 * Per design.md: Premium UI with semantic tokens, lucide icons, no emojis.
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Card, CardContent,
    Button, Badge, Input, Skeleton,
    Plus, Search, FolderOpen, Trash2, Edit, Link, ExternalLink,
} from '@uprosal/ui';
import { ProofDialog } from './ProofDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type CategoryFilter = 'all' | 'portfolio' | 'testimonial' | 'case_study' | 'certification';

const CATEGORY_LABELS: Record<string, string> = {
    portfolio: 'Portfolio',
    testimonial: 'Testimonial',
    case_study: 'Case Study',
    certification: 'Certification',
};

export function ProofPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProof, setEditingProof] = useState<Doc<'proofItems'> | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Doc<'proofItems'> | null>(null);

    // Fetch proof items from Convex
    const proofItems = useQuery(api.queries.proofItems.list, {});
    const archiveProof = useMutation(api.mutations.proofItems.archive);

    // Filter proof items
    const filteredItems = proofItems?.filter((item: Doc<'proofItems'>) => {
        // Category filter
        if (categoryFilter !== 'all' && item.category !== categoryFilter) {
            return false;
        }

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                item.title.toLowerCase().includes(query) ||
                item.description.toLowerCase().includes(query) ||
                item.tags.some(t => t.includes(query))
            );
        }

        return true;
    }) ?? [];

    const handleNewProof = () => {
        setEditingProof(null);
        setDialogOpen(true);
    };

    const handleEditProof = (proof: Doc<'proofItems'>) => {
        setEditingProof(proof);
        setDialogOpen(true);
    };

    const handleDeleteProof = (proof: Doc<'proofItems'>) => {
        setDeleteConfirm(proof);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await archiveProof({ proofId: deleteConfirm._id });
        } catch (error) {
            console.error('Failed to archive proof item:', error);
        }
        setDeleteConfirm(null);
    };

    const isLoading = proofItems === undefined;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Proof Library</h1>
                    <p className="text-muted-foreground mt-1">
                        Portfolio items, case studies, and testimonials to enrich your proposals
                    </p>
                </div>
                <Button className="flex items-center gap-2" onClick={handleNewProof}>
                    <Plus className="w-4 h-4" />
                    Add Proof
                </Button>
            </div>

            {/* Category Filter Tabs */}
            <div className="flex gap-1 border-b border-border overflow-x-auto">
                {(['all', 'portfolio', 'testimonial', 'case_study', 'certification'] as CategoryFilter[]).map((cat) => (
                    <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap ${categoryFilter === cat
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
                        {categoryFilter === cat && (
                            <motion.div
                                layoutId="categoryTab"
                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder="Search by title, description, or tags..."
                        className="w-full pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                {proofItems && (
                    <Badge variant="secondary" className="shrink-0">
                        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                    </Badge>
                )}
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Card key={i}>
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between mb-3">
                                    <Skeleton className="w-10 h-10 rounded-lg" />
                                    <Skeleton className="w-6 h-6" />
                                </div>
                                <Skeleton className="h-5 w-32 mb-2" />
                                <Skeleton className="h-4 w-full mb-1" />
                                <Skeleton className="h-4 w-2/3 mb-3" />
                                <div className="flex gap-2">
                                    <Skeleton className="h-5 w-16" />
                                    <Skeleton className="h-5 w-16" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredItems.length === 0 && (
                <Card className="py-12">
                    <CardContent className="text-center">
                        <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                            <FolderOpen className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {searchQuery || categoryFilter !== 'all'
                                ? 'No matching proof items'
                                : 'No proof items yet'}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            {searchQuery || categoryFilter !== 'all'
                                ? 'Try a different search term or category.'
                                : 'Add portfolio pieces, case studies, and testimonials to enhance your proposals.'}
                        </p>
                        {!searchQuery && categoryFilter === 'all' && (
                            <Button onClick={handleNewProof}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Your First Proof Item
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Proof Items Grid */}
            <AnimatePresence mode="wait">
                {!isLoading && filteredItems.length > 0 && (
                    <motion.div
                        key="proof-grid"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {filteredItems.map((item: Doc<'proofItems'>, i: number) => (
                            <motion.div
                                key={item._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: i * 0.05 }}
                                className="h-full"
                            >
                                <Card className="group cursor-pointer hover:shadow-lg transition-shadow h-full flex flex-col">
                                    <CardContent className="pt-6 flex flex-col flex-1">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <FolderOpen className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {item.url && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        title="Open link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(item.url, '_blank');
                                                        }}
                                                    >
                                                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                    title="Edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditProof(item);
                                                    }}
                                                >
                                                    <Edit className="w-4 h-4 text-muted-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                    title="Delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteProof(item);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                                                </Button>
                                            </div>
                                        </div>

                                        <h3
                                            className="font-semibold text-foreground mb-1 truncate max-w-full"
                                            onClick={() => handleEditProof(item)}
                                            title={item.title}
                                        >
                                            {item.title}
                                        </h3>

                                        {/* Description */}
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem]">
                                            {item.description}
                                        </p>

                                        {/* URL preview */}
                                        {item.url && (
                                            <div className="flex items-center gap-1.5 text-xs text-primary mb-3 truncate">
                                                <Link className="w-3 h-3 shrink-0" />
                                                <span className="truncate">{item.url}</span>
                                            </div>
                                        )}

                                        {/* Tags */}
                                        <div className="flex flex-wrap gap-1 mb-3 min-h-[1.5rem]">
                                            {item.category && (
                                                <Badge variant="outline" className="text-xs">
                                                    {CATEGORY_LABELS[item.category] || item.category}
                                                </Badge>
                                            )}
                                            {item.tags.slice(0, 3).map((tag) => (
                                                <Badge key={tag} variant="secondary" className="text-xs">
                                                    {tag}
                                                </Badge>
                                            ))}
                                            {item.tags.length > 3 && (
                                                <Badge variant="secondary" className="text-xs">
                                                    +{item.tags.length - 3}
                                                </Badge>
                                            )}
                                        </div>

                                        {/* Footer */}
                                        <div className="flex items-center justify-between text-xs mt-auto">
                                            <span className="text-muted-foreground">
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Create/Edit Dialog */}
            <ProofDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                proof={editingProof}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={!!deleteConfirm}
                onOpenChange={(open) => !open && setDeleteConfirm(null)}
                title="Delete Proof Item"
                description={
                    <>
                        Are you sure you want to delete "{deleteConfirm?.title}"?
                        <br /><br />
                        This action cannot be undone.
                    </>
                }
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={confirmDelete}
            />
        </div>
    );
}
